import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getTrialUpgradePrompt, recordTrialUpgradePromptOp, TrialUpgradePromptResponse } from '@extension/wa-communication-layer/api/api-calls';
import { shouldShowTrialUpgradePrompt, trialUpgradeCheckoutUrl } from './trial-upgrade-prompt-logic';

type ShowableResponse = TrialUpgradePromptResponse & {
  type: NonNullable<TrialUpgradePromptResponse['type']>;
  plan: NonNullable<TrialUpgradePromptResponse['plan']>;
  copy: NonNullable<TrialUpgradePromptResponse['copy']>;
};

/**
 * #1226 slice A: one dismissible in-trial upgrade popup. All copy comes from the server
 * (single source of truth in the backend). Both buttons are the same size and style, so
 * "Not now" carries equal weight to the primary action. Renders nothing unless the server
 * says show:true.
 */
export const TrialUpgradePrompt: React.FC = () => {
  const [prompt, setPrompt] = useState<ShowableResponse | null>(null);
  const [open, setOpen] = useState(false);
  const fetchedRef = useRef(false);
  const shownRecordedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getTrialUpgradePrompt()
      .then((res) => {
        if (shouldShowTrialUpgradePrompt(res)) {
          setPrompt(res);
          setOpen(true);
        }
      })
      .catch(() => { /* a failed decision check never blocks the app: no popup */ });
  }, []);

  // Record `shown` once, when the popup actually appears (server validates the arm).
  useEffect(() => {
    if (!open || !prompt || shownRecordedRef.current) return;
    shownRecordedRef.current = true;
    recordTrialUpgradePromptOp('shown', prompt.type).catch(() => {});
  }, [open, prompt]);

  if (!prompt) return null;

  const close = (): void => setOpen(false);

  const onPrimary = (): void => {
    recordTrialUpgradePromptOp('clicked', prompt.type).catch(() => {});
    window.open(trialUpgradeCheckoutUrl(prompt.plan), '_blank', 'noopener');
    close();
  };

  const onNotNow = (): void => {
    recordTrialUpgradePromptOp('dismissed', prompt.type).catch(() => {});
    close();
  };

  // Equal weight: identical variant, size and class for both buttons.
  const equalButtonClass = 'min-w-[9rem] flex-1 h-auto whitespace-normal py-2';

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onNotNow(); }}>
      <DialogContent className="max-w-md" data-testid="trial-upgrade-prompt">
        <DialogHeader>
          <DialogTitle>{prompt.copy.heading}</DialogTitle>
          <DialogDescription>{prompt.copy.body}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{prompt.copy.footer}</p>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className={equalButtonClass} onClick={onNotNow}>
            {prompt.copy.notNow}
          </Button>
          <Button variant="outline" className={equalButtonClass} onClick={onPrimary}>
            {prompt.copy.button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
